import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import Button from "~/components/common/button";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import Constants from "~/utils/constants";

import styles from "./purchases.module.scss";
import PurchasesItem from "./purchases-item";

type Props = {
  returns?: boolean;
};

export default function Orders(props: Props) {
  const { user, isAuthenticated } = useAuthContext();
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const getWebshops = hooks.useCallService({ service: Core.GetWebshops });
  const listOrders = hooks.useCallService({ service: Core.ListOrdersForCustomer });
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);

  const query = useInfiniteQuery({
    queryKey: ["getOrdersList", user?.ID, !!props.returns],
    staleTime: Constants.DEFAULT_STALE_TIME,
    keepPreviousData: true,
    queryFn: async ({ pageParam = 0 }) => {
      const orderList = await listOrders({
        ID: user!.ID,
        PageConfig: {
          Start: pageParam,
          SortDirection: 1,
          // SortProperty: "PlacementDate",
          Limit: Constants.ORDERS_PAGE_SIZE,
          Filter: {
            ShowOrdersWithoutLines: true,
            OrderProperties: props.returns ? Constants.RETURN_ORDER : undefined,
            ExcludeOrderProperties: !props.returns ? Constants.RETURN_ORDER : undefined,
          },
        },
      });
      if (orderList) {
        return orderList.Result;
      }
      return null;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage && lastPage.NumberOfPages > lastPage.CurrentPage) {
        return lastPage.PageConfig.Start! + lastPage.Limit;
      }

      return undefined;
    },
    enabled: !!(evaEndpointUrl && isAuthenticated),
  });

  const { data: webshops } = useQuery({
    queryKey: ["getWebshops"],
    staleTime: Infinity,
    queryFn: async () => await getWebshops(),
    enabled: !!evaEndpointUrl,
  });

  const purchases = query.data?.pages
    .flatMap((page) => page?.Page ?? [])
    .filter((order) => {
      const filteredLines = order.Lines.filter(
        (line) => !!line.ProductID && ((line.IsCancelled && line.FulfillmentStatus === 800) || !line.IsCancelled)
      );
      return (
        filteredLines.length > 0 &&
        (props.returns ||
          order.Payments.length > 0 ||
          filteredLines.some((line) => line.ShippingMethod?.PaymentHandledByCarrier))
      );
    });

  return (
    <>
      {breakpoint == "desktop" && (
        <h2 className={styles.purchasesTitle}>
          {t(props.returns ? "generic.myaccount.nav.returns" : "generic.myaccount.nav.purchases")}
          {/* ({query.data ? query.data.pages[0]?.Total : 0}) */}
        </h2>
      )}
      <div className={styles.purchasesList}>
        {purchases?.length == 0 && (
          <p className={styles.noOrdersYet}>{t(props.returns ? "generic.noReturnsYet" : "generic.noOrdersYet")}</p>
        )}
        {purchases?.map((order) => {
          return <PurchasesItem key={order.ID} order={order} returns={props.returns} webshops={webshops} />;
        })}
      </div>
      {query.hasNextPage && (
        <Button
          variant="secondary"
          className={styles.nextPage}
          disabled={query.isFetchingNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {t("generic.load_more")}
        </Button>
      )}
    </>
  );
}
